import Divider from "@mui/material/Divider";

import ErrorState from "@app/analytics/components/ErrorState";
import { MetricGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import TableCell from "@app/analytics/components/Table/TableCell";
import { getBiasDescription } from "@app/analytics/utils";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingSession } from "@app/common/types";
import { getSemanticScore } from "@app/common/utils";

import { Column, Row } from "@rui/Stack";

import CoachingTable from "./components/CoachingTable";
import useCoachingDashboardCharismaBias from "./hooks/useCoachingDashboardCharismaBias";
import {
  Card,
  CardText,
  EmptyCardText,
  LoadingSkeleton,
  SectionDescription,
  SectionHeading,
  SectionOverviewContainer,
  SectionSubHeading,
  ValueWithSemanticIcon,
} from "./styles";

const BIAS_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "bias",
    label: "Your Bias",
    sortBy: (d) => d.bias.value,
  },
  {
    id: "charisma",
    label: "Your Charisma",
    sortBy: (d) => d.charisma.value,
  },
];

const Bias = () => {
  const { bias, isError, isInitialLoading, refetch } =
    useCoachingDashboardCharismaBias();

  if (isError) {
    return <ErrorState refetch={refetch} />;
  }

  return (
    <>
      <Column spacing={1}>
        <Row variant="apart">
          <SectionHeading>Bias</SectionHeading>
          <ValueWithSemanticIcon
            score={bias.semanticScore}
            variant="h3"
            sx={{ fontWeight: 500 }}
          >
            {bias.average}
          </ValueWithSemanticIcon>
        </Row>
        <SectionDescription>
          {getBiasDescription(bias.average)}
        </SectionDescription>
        <SectionOverviewContainer spacing={1}>
          <Card title="Range" subtitle="Avg bias / meeting">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : bias.average ? (
              <MetricGradientRange value={bias.average} />
            ) : (
              <EmptyCardText />
            )}
          </Card>
          <Card title="Your highest bias">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : bias.participant ? (
              <CardText>
                {bias.participant.name} ({bias.participant.value})
              </CardText>
            ) : (
              <EmptyCardText />
            )}
          </Card>
        </SectionOverviewContainer>
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Meetings</SectionSubHeading>
        <SectionDescription>
          Review meeting reports where you showed bias towards others.
        </SectionDescription>
        <CoachingTable
          defaultOrderBy="bias"
          defaultOrder="asc"
          isLoading={isInitialLoading}
          renderSectionCells={(session) => (
            <>
              <TableCell>
                <ValueWithSemanticIcon
                  score={getSemanticScore(session.bias.value)}
                  variant="bodySecondaryReg"
                  sx={{ minWidth: "30px", textAlign: "right" }}
                >
                  {session.bias.value}
                </ValueWithSemanticIcon>
              </TableCell>
              <TableCell>{session.charisma.value ?? "-"}</TableCell>
            </>
          )}
          rows={bias.sessions}
          sectionHeadCells={BIAS_HEAD_CELLS}
        />
      </Column>
    </>
  );
};

export default Bias;
